<template>
    <!-- Stays in the parent (button/link) so we can bind hover to that host. -->
    <span ref="anchorRef" class="pointer-events-none absolute h-0 w-0 overflow-hidden" aria-hidden="true"></span>
    <Teleport to="body">
        <div
            v-show="visible"
            ref="tipRef"
            class="pointer-events-none fixed z-[500] w-max max-w-[min(22rem,calc(100vw-1rem))] rounded bg-gray-900 px-2 py-1 text-white shadow-lg ring-1 ring-white/15"
            :class="fontClass"
            :style="tipStyle"
        >
            {{ text }}
        </div>
    </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
    text: String,
    direction: {
        type: String,
        default: 'right',
    },
    fixed: {
        type: Boolean,
        default: false,
    },
    font: {
        type: String,
        default: 'small',
    },
});

const anchorRef = ref(null);
const tipRef = ref(null);
const visible = ref(false);
const tipStyle = ref({ top: '0px', left: '0px' });
let hostEl = null;

const fontClass = computed(() => (props.font === 'small' ? 'text-xs' : 'text-sm'));

const placeTip = async () => {
    if (!hostEl) return;
    await nextTick();
    const rect = hostEl.getBoundingClientRect();
    const tip = tipRef.value?.getBoundingClientRect?.() || { width: 80, height: 24 };
    const gap = 8;
    let top = rect.top;
    let left = rect.left;

    switch (props.direction) {
        case 'top':
            top = rect.top - tip.height - gap;
            // Not enough room above — drop below rather than run off the top.
            if (top < gap) top = rect.bottom + gap;
            left = rect.left + rect.width / 2 - tip.width / 2;
            break;
        case 'bottom':
            top = rect.bottom + gap;
            if (top + tip.height > window.innerHeight - gap) top = rect.top - tip.height - gap;
            left = rect.left + rect.width / 2 - tip.width / 2;
            break;
        case 'left':
            top = rect.top + rect.height / 2 - tip.height / 2;
            left = rect.left - tip.width - gap;
            if (left < gap) left = rect.right + gap;
            break;
        case 'right':
        default:
            top = rect.top + rect.height / 2 - tip.height / 2;
            left = rect.right + gap;
            if (left + tip.width > window.innerWidth - gap) left = rect.left - tip.width - gap;
            break;
    }

    // Keep the whole bubble on screen. Long explainer text is wide, so a
    // top-left-only clamp would let the right edge run past the viewport.
    tipStyle.value = {
        top: `${Math.round(clamp(top, tip.height, window.innerHeight))}px`,
        left: `${Math.round(clamp(left, tip.width, window.innerWidth))}px`,
    };
};

/** Pin `start` so a `size`-long box stays inside `limit`, 4px off either edge. */
const clamp = (start, size, limit) => Math.max(4, Math.min(start, limit - size - 4));

const onEnter = () => {
    visible.value = true;
    placeTip();
};

const onLeave = () => {
    visible.value = false;
};

const onReposition = () => {
    if (visible.value) placeTip();
};

onMounted(() => {
    hostEl = anchorRef.value?.parentElement || null;
    if (!hostEl) return;
    if (getComputedStyle(hostEl).position === 'static') {
        hostEl.style.position = 'relative';
    }
    hostEl.addEventListener('mouseenter', onEnter);
    hostEl.addEventListener('mouseleave', onLeave);
    hostEl.addEventListener('focusin', onEnter);
    hostEl.addEventListener('focusout', onLeave);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
});

onUnmounted(() => {
    window.removeEventListener('scroll', onReposition, true);
    window.removeEventListener('resize', onReposition);
    if (!hostEl) return;
    hostEl.removeEventListener('mouseenter', onEnter);
    hostEl.removeEventListener('mouseleave', onLeave);
    hostEl.removeEventListener('focusin', onEnter);
    hostEl.removeEventListener('focusout', onLeave);
});
</script>
